import { differenceInYears, format, isValid, parseISO } from 'date-fns';

import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

type DateFormat
  = | 'date'
    | 'weekday'
    | 'time'
    | 'datetime'
    | 'YYYY-MM-DD'
    | 'DD [de] MMMM [de] YYYY'
    | 'DD/MM/YYYY [às] HH:mm'
    | 'DD/MM [às] HH:mm'
    | 'DD/MM'
    | 'HH:mm';

const defaultTimeZone = 'America/Sao_Paulo';

function isValidDate(dateObject: unknown): dateObject is Date {
  return dateObject instanceof Date && !Number.isNaN(dateObject);
}

export const createFromDatePartString = (dateString: string) => {
  if (dateString.length !== 10) {
    throw new Error('Invalid date string');
  }
  // se for uma data sem horário, vai criar no horário de Brasília
  const dateObject = new Date(`${dateString}T00:00:00-03:00`);
  return dateObject;
};

export const toDate = (date: string | Date) => {
  const dateObject = new Date(date);
  if (isValidDate(dateObject)) {
    return dateObject;
  }
  throw new Error('Invalid date');
};

export const toRawDateString = (date: string) => {
  return date.split('T')[0].split('-').reverse().join('').replace(/-/g, '');
};

export const dateFormatter = (value?: string | Date, format: DateFormat = 'date') => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    if (value.length === 10) {
      value = createFromDatePartString(value);
    }
  }

  const dateObject = new Date(value);
  if (!isValidDate(dateObject)) {
    return '';
  }

  if (format === 'date') {
    return dateObject.toLocaleDateString('pt-br', {
      timeZone: defaultTimeZone,
    });
  }

  if (format === 'weekday') {
    return dateObject
      .toLocaleDateString('pt-br', {
        weekday: 'short',
        timeZone: defaultTimeZone,
      })
      .slice(0, 3);
  }

  if (format === 'time' || format === 'HH:mm') {
    return dateObject.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: defaultTimeZone,
    });
  }

  if (format === 'datetime') {
    return dateObject
      .toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: defaultTimeZone,
      })
      .replace(/, /g, ' ');
  }

  if (format === 'YYYY-MM-DD') {
    return dateObject
      .toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: defaultTimeZone,
      })
      .split('/')
      .reverse()
      .join('-');
  }

  if (format === 'DD [de] MMMM [de] YYYY') {
    return dateObject.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: defaultTimeZone,
    });
  }

  if (format === 'DD/MM/YYYY [às] HH:mm') {
    const datePart = dateObject.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: defaultTimeZone,
    });

    const timePart = dateObject.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: defaultTimeZone,
    });

    return `${datePart} às ${timePart}`;
  }

  if (format === 'DD/MM [às] HH:mm') {
    const datePart = dateObject.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      timeZone: defaultTimeZone,
    });

    const timePart = dateObject.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: defaultTimeZone,
    });

    return `${datePart} às ${timePart}`;
  }

  if (format === 'DD/MM') {
    const datePart = dateObject.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      timeZone: defaultTimeZone,
    });

    return `${datePart}`;
  }

  return dateObject
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: defaultTimeZone,
    })
    .replace(/, /g, ' ');
};

export function formatDateToYMD(date: Date | undefined): string | undefined {
  if (!date || !isValid(date)) {
    return undefined;
  }
  return format(date, 'yyyy-MM-dd');
}

export function formatDateToBR(date: Date | undefined): string | undefined {
  if (!date || !isValid(date)) {
    return undefined;
  }
  return format(date, 'dd/MM/yyyy');
}

export const getAgeFromBirthday = (birthday: string): number => {
  return differenceInYears(new Date(), parseISO(birthday.slice(0, 10)));
};

interface FormatAppointmentDatetimeProps {
  scheduleType?: ScheduleType | null;
  start?: string | Date;
  end?: string | Date;
}

export const formatDateSchedule = (newDateTime: { start?: string; end?: string }): string => {
  if (!newDateTime.start) {
    return '';
  }

  return newDateTime.end
    ? `${newDateTime.start} às ${newDateTime.end} (Horário de Brasília), por ordem de chegada`
    : `${newDateTime.start} (Horário de Brasília)`;
};

export const formatAppointmentDatetime = ({ scheduleType, start, end }: FormatAppointmentDatetimeProps): string => {
  if (!scheduleType || !start) {
    return '';
  }
  const startTime = dateFormatter(start, 'HH:mm');
  const endTime = scheduleType != ScheduleType.SCHEDULED ? dateFormatter(end, 'HH:mm') : undefined;

  return formatDateSchedule({
    start: startTime,
    end: endTime,
  });
};
